import { useRef, useState } from 'react';
import { Download, FileText, FileType, Sheet, Globe, Braces, Code2, FileDown } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Markdown } from '../../components/ui/Markdown';
import { artifactsService } from '../../services/artifactsService';
import { toast } from '../../store/uiStore';
import type { Artifact } from '../../types';

const TYPE_LABEL: Record<Artifact['type'], string> = {
  html: 'HTML', document: 'Documento', table: 'Planilha', json: 'JSON', code: 'Código', image: 'Imagem',
};

// Visualizador real: preview por tipo + barra de exportação.
export function ArtifactViewer({ artifact }: { artifact: Artifact }) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function run(key: string, fn: () => Promise<void> | void) {
    setBusy(key);
    try {
      await fn();
    } catch {
      toast.error('Falha ao exportar.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge tone="navy">{TYPE_LABEL[artifact.type]}</Badge>
        <Badge tone="neutral">v{artifact.version}</Badge>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {(artifact.type === 'html' || artifact.type === 'document') && (
            <>
              <Button size="sm" variant="ghost" icon={FileDown} loading={busy === 'pdf'} onClick={() => run('pdf', () => artifactsService.exportServer(artifact, 'pdf'))}>PDF</Button>
              <Button size="sm" variant="ghost" icon={FileType} loading={busy === 'docx'} onClick={() => run('docx', () => artifactsService.exportServer(artifact, 'docx'))}>DOCX</Button>
            </>
          )}
          {artifact.type === 'html' && (
            <Button size="sm" variant="ghost" icon={Globe} onClick={() => artifactsService.exportText(artifact, 'html', 'text/html')}>HTML</Button>
          )}
          {artifact.type === 'document' && (
            <Button size="sm" variant="ghost" icon={FileText} onClick={() => artifactsService.exportText(artifact, 'md', 'text/markdown')}>MD</Button>
          )}
          {artifact.type === 'table' && (
            <>
              <Button size="sm" variant="ghost" icon={Sheet} loading={busy === 'xlsx'} onClick={() => run('xlsx', () => artifactsService.exportServer(artifact, 'xlsx'))}>XLSX</Button>
              <Button size="sm" variant="ghost" icon={Download} onClick={() => artifactsService.exportText(artifact, 'csv', 'text/csv')}>CSV</Button>
            </>
          )}
          {artifact.type === 'json' && (
            <>
              <Button size="sm" variant="ghost" icon={Sheet} loading={busy === 'xlsx'} onClick={() => run('xlsx', () => artifactsService.exportServer(artifact, 'xlsx'))}>XLSX</Button>
              <Button size="sm" variant="ghost" icon={Braces} onClick={() => artifactsService.exportText(artifact, 'json', 'application/json')}>JSON</Button>
            </>
          )}
          {artifact.type === 'code' && (
            <Button size="sm" variant="ghost" icon={Code2} onClick={() => artifactsService.exportText(artifact, artifact.language || 'txt', 'text/plain')}>Baixar</Button>
          )}
        </div>
      </div>

      <ArtifactPreview artifact={artifact} previewRef={previewRef} />
    </div>
  );
}

function ArtifactPreview({ artifact, previewRef }: { artifact: Artifact; previewRef: React.RefObject<HTMLDivElement> }) {
  if (artifact.type === 'html') {
    // Libera interatividade total (scripts, modais alert/confirm/prompt, formulários,
    // popups e downloads) — porém SEM allow-same-origin, então a página roda num origin
    // isolado e não acessa o app pai (não lê token/localStorage).
    return (
      <iframe
        title={artifact.name}
        sandbox="allow-scripts allow-modals allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
        srcDoc={prepareHtmlForPreview(artifact.content)}
        className="h-[70vh] w-full rounded-xl border border-line bg-white"
      />
    );
  }

  if (artifact.type === 'table') {
    const rows = parseCsv(artifact.content);
    return (
      <div ref={previewRef} className="max-h-[60vh] overflow-auto scrollbar-thin rounded-xl border border-line bg-white p-4">
        <CsvTable rows={rows} />
      </div>
    );
  }

  if (artifact.type === 'json') {
    let pretty = artifact.content;
    try { pretty = JSON.stringify(JSON.parse(artifact.content), null, 2); } catch { /* cru */ }
    return (
      <div ref={previewRef} className="max-h-[60vh] overflow-auto scrollbar-thin rounded-xl border border-line bg-cloud p-4">
        <pre className="font-mono text-xs leading-relaxed text-ink">{pretty}</pre>
      </div>
    );
  }

  if (artifact.type === 'code') {
    return (
      <div ref={previewRef} className="max-h-[60vh] overflow-auto scrollbar-thin rounded-xl border border-line bg-navy p-4">
        <pre className="font-mono text-xs leading-relaxed text-white"><code>{artifact.content}</code></pre>
      </div>
    );
  }

  // document → markdown renderizado
  return (
    <div ref={previewRef} className="max-h-[60vh] overflow-auto scrollbar-thin rounded-xl border border-line bg-white p-5 text-sm text-ink">
      <Markdown>{artifact.content}</Markdown>
    </div>
  );
}

// Injeta no preview: links abrem em nova aba e cliques em "#"/forms sem ação não
// "navegam" o iframe (o que deixaria o preview em branco). Não altera o artefato salvo.
const PREVIEW_INJECT = `<base target="_blank"><script>(function(){
  document.addEventListener('click',function(e){var a=e.target&&e.target.closest?e.target.closest('a'):null;if(!a)return;var h=a.getAttribute('href');if(h===null||h===''||h==='#'){e.preventDefault();}},true);
  document.addEventListener('submit',function(e){var f=e.target;if(f&&!f.getAttribute('action')){e.preventDefault();}},true);
})();<\/script>`;

function prepareHtmlForPreview(html: string): string {
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + PREVIEW_INJECT);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => m + PREVIEW_INJECT);
  return PREVIEW_INJECT + html;
}

function parseCsv(text: string): string[][] {
  return text.trim().split(/\r?\n/).map((l) => l.split(',').map((c) => c.trim()));
}

function CsvTable({ rows }: { rows: string[][] }) {
  if (!rows.length) return <p className="text-sm text-slate">Tabela vazia.</p>;
  const [head, ...body] = rows;
  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-line">
          {head.map((c, i) => <th key={i} className="px-2 py-1.5 font-semibold text-ink">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {body.map((r, i) => (
          <tr key={i} className="border-b border-line/60">
            {r.map((c, j) => <td key={j} className="px-2 py-1.5 text-slate">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Modal reutilizável (chat + aba Artefatos).
export function ArtifactModal({ artifact, open, onClose }: { artifact: Artifact | null; open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title={artifact?.name} width="max-w-4xl">
      {artifact && <ArtifactViewer artifact={artifact} />}
    </Modal>
  );
}
